import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { format, isAfter, isBefore, addDays, subDays } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface Notification {
  id: string;
  type: 'todo' | 'followup';
  priority: 'overdue' | 'urgent' | 'reminder';
  title: string;
  description: string;
  date: Date;
  customerId?: string;
  unitId?: string;
}

export const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [dismissedNotifications, setDismissedNotifications] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(false);
  const [swipeStates, setSwipeStates] = useState<Record<string, { startX: number; currentX: number; isSwiping: boolean }>>({});
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const fetchNotifications = async () => {
    if (!user) return;

    const now = new Date();
    const tomorrow = addDays(now, 1);
    const twoDaysFromNow = addDays(now, 2);
    const threeDaysFromNow = addDays(now, 3);

    const allNotifications: Notification[] = [];

    // Fetch todos
    const { data: todos } = await supabase
      .from('todos')
      .select('*, customers(first_name, last_name)')
      .eq('user_id', user.id)
      .eq('completed', false)
      .not('due_date', 'is', null);

    if (todos) {
      todos.forEach((todo) => {
        const dueDate = new Date(todo.due_date);
        const customerName = todo.customers 
          ? `${todo.customers.first_name} ${todo.customers.last_name}`.trim()
          : 'Unbekannter Kunde';

        // Overdue todos
        if (isBefore(dueDate, now)) {
          allNotifications.push({
            id: `todo-overdue-${todo.id}`,
            type: 'todo',
            priority: 'overdue',
            title: 'Überfälliges Todo',
            description: `${todo.title} - ${customerName}`,
            date: dueDate,
            customerId: todo.customer_id,
          });
        }
        // Urgent todos (2 days before due)
        else if (isBefore(dueDate, threeDaysFromNow) && isAfter(dueDate, tomorrow)) {
          allNotifications.push({
            id: `todo-urgent-${todo.id}`,
            type: 'todo',
            priority: 'urgent',
            title: 'Todo fällig in 2-3 Tagen',
            description: `${todo.title} - ${customerName}`,
            date: dueDate,
            customerId: todo.customer_id,
          });
        }
      });
    }

    // Fetch D2D units with follow-ups
    const { data: units } = await supabase
      .from('d2d_units')
      .select(`
        *,
        d2d_houses(
          house_number,
          d2d_streets(
            name,
            d2d_areas(name)
          )
        )
      `)
      .eq('user_id', user.id)
      .not('next_reminder', 'is', null);

    if (units) {
      units.forEach((unit) => {
        const reminderDate = new Date(unit.next_reminder);
        const address = `${unit.d2d_houses?.d2d_streets?.d2d_areas?.name || ''} ${unit.d2d_houses?.d2d_streets?.name || ''} ${unit.d2d_houses?.house_number || ''}${unit.unit_name ? ` - ${unit.unit_name}` : ''}`.trim();

        // Überprüfe auf überfällige Follow-ups (Zeitstempel-Vergleich für genauere Erkennung)
        if (reminderDate.getTime() < now.getTime()) {
          allNotifications.push({
            id: `followup-overdue-${unit.id}`,
            type: 'followup',
            priority: 'overdue',
            title: 'Überfälliger Follow-up',
            description: address,
            date: reminderDate,
            unitId: unit.id,
          });
        }
        // Follow-ups die heute oder morgen fällig sind
        else if (reminderDate.getTime() <= twoDaysFromNow.getTime()) {
          const isToday = reminderDate.getTime() < tomorrow.getTime();
          allNotifications.push({
            id: `followup-reminder-${unit.id}`,
            type: 'followup',
            priority: isToday ? 'urgent' : 'reminder',
            title: isToday ? 'Follow-up heute fällig' : 'Follow-up bald fällig',
            description: address,
            date: reminderDate,
            unitId: unit.id,
          });
        }
      });
    }

    // Sort by priority and date
    const priorityOrder = { overdue: 0, urgent: 1, reminder: 2 };
    allNotifications.sort((a, b) => {
      if (a.priority !== b.priority) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      return a.date.getTime() - b.date.getTime();
    });

    setNotifications(allNotifications);
  };

  useEffect(() => {
    fetchNotifications();
    
    // Refresh notifications every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const handleNotificationClick = (notification: Notification) => {
    if (notification.type === 'todo' && notification.customerId) {
      navigate('/customer-management', { 
        state: { selectedCustomerId: notification.customerId } 
      });
    } else if (notification.type === 'followup' && notification.unitId) {
      // Navigate to D2D and open the specific unit
      navigate('/', { 
        state: { openUnitId: notification.unitId } 
      });
    }
    setIsOpen(false);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'overdue': return 'text-destructive';
      case 'urgent': return 'text-orange-500';
      case 'reminder': return 'text-blue-500';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityBadgeVariant = (priority: string): "default" | "secondary" | "destructive" => {
    switch (priority) {
      case 'overdue': return 'destructive';
      case 'urgent': return 'default';
      case 'reminder': return 'secondary';
      default: return 'secondary';
    }
  };

  const handleTouchStart = (e: React.TouchEvent, notificationId: string) => {
    const touch = e.touches[0];
    setSwipeStates(prev => ({
      ...prev,
      [notificationId]: {
        startX: touch.clientX,
        currentX: touch.clientX,
        isSwiping: true
      }
    }));
  };

  const handleTouchMove = (e: React.TouchEvent, notificationId: string) => {
    if (!swipeStates[notificationId]?.isSwiping) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeStates[notificationId].startX;
    
    // Only allow left swipe (negative deltaX)
    if (deltaX < 0) {
      setSwipeStates(prev => ({
        ...prev,
        [notificationId]: {
          ...prev[notificationId],
          currentX: touch.clientX
        }
      }));
    }
  };

  const handleTouchEnd = (notificationId: string) => {
    const swipeState = swipeStates[notificationId];
    if (!swipeState) return;

    const deltaX = swipeState.currentX - swipeState.startX;
    const swipeThreshold = -100; // Pixels to swipe left for dismiss

    if (deltaX < swipeThreshold) {
      handleDismissNotification(notificationId);
    }

    setSwipeStates(prev => {
      const newState = { ...prev };
      delete newState[notificationId];
      return newState;
    });
  };

  const handleDismissNotification = (notificationId: string) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification) return;

    setDismissedNotifications(prev => new Set([...prev, notificationId]));
    
    toast({
      title: "Benachrichtigung entfernt",
      description: `${notification.title} wurde entfernt`,
      action: (
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => handleUndoDismiss(notificationId)}
        >
          Rückgängig
        </Button>
      ),
    });
  };

  const handleUndoDismiss = (notificationId: string) => {
    setDismissedNotifications(prev => {
      const newSet = new Set(prev);
      newSet.delete(notificationId);
      return newSet;
    });
  };

  const getSwipeTransform = (notificationId: string) => {
    const swipeState = swipeStates[notificationId];
    if (!swipeState?.isSwiping) return 'translateX(0px)';
    
    const deltaX = swipeState.currentX - swipeState.startX;
    // Only show negative translation (left swipe)
    return `translateX(${Math.min(deltaX, 0)}px)`;
  };

  const getSwipeOpacity = (notificationId: string) => {
    const swipeState = swipeStates[notificationId];
    if (!swipeState?.isSwiping) return 1;
    
    const deltaX = swipeState.currentX - swipeState.startX;
    const maxSwipe = -150;
    return Math.max(0.3, 1 + deltaX / maxSwipe);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative hover:bg-transparent hover:text-current">
          <Bell className="h-4 w-4" />
          {notifications.length > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {notifications.length > 99 ? '99+' : notifications.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Benachrichtigungen</h3>
          <p className="text-sm text-muted-foreground">
            {notifications.length} {notifications.length === 1 ? 'Benachrichtigung' : 'Benachrichtigungen'}
          </p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.filter(n => !dismissedNotifications.has(n.id)).length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              Keine Benachrichtigungen
            </div>
          ) : (
            <div className="divide-y">
              {notifications
                .filter(n => !dismissedNotifications.has(n.id))
                .map((notification) => (
                <div
                  key={notification.id}
                  className="relative overflow-hidden"
                >
                  <div
                    className="p-4 hover:bg-white/5 hover:backdrop-blur-sm cursor-pointer transition-all duration-300 select-none relative"
                    style={{
                      transform: getSwipeTransform(notification.id),
                      opacity: getSwipeOpacity(notification.id)
                    }}
                    onClick={() => handleNotificationClick(notification)}
                    onTouchStart={(e) => handleTouchStart(e, notification.id)}
                    onTouchMove={(e) => handleTouchMove(e, notification.id)}
                    onTouchEnd={() => handleTouchEnd(notification.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {notification.title}
                          </span>
                          <Badge 
                            variant={getPriorityBadgeVariant(notification.priority)}
                            className="text-xs"
                          >
                            {notification.priority === 'overdue' ? 'Überfällig' : 
                             notification.priority === 'urgent' ? 'Dringend' : 'Erinnerung'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {notification.description}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(notification.date, 'dd.MM.yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                  </div>
                  {swipeStates[notification.id]?.isSwiping && getSwipeTransform(notification.id) !== 'translateX(0px)' && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-red-500 font-medium pointer-events-none">
                      Entfernen
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};